import React, { useState } from "react";
import {
  Box,
  Loader,
  Popover,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import styles from "./sales.module.css";
import { SalesSearchInput, MenuToken, ResultCount } from "./salesToolbarParts";

export type GiftCardStatusFilter =
  | "all"
  | "active"
  | "depleted"
  | "expired"
  | "voided";
export type GiftCardClaimedFilter = "all" | "claimed" | "unclaimed";

const STATUS_OPTIONS: { value: GiftCardStatusFilter; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "depleted", label: "Depleted" },
  { value: "expired", label: "Expired" },
  { value: "voided", label: "Voided" },
];

const CLAIMED_OPTIONS: { value: GiftCardClaimedFilter; label: string }[] = [
  { value: "all", label: "Claimed & unclaimed" },
  { value: "claimed", label: "Claimed" },
  { value: "unclaimed", label: "Unclaimed" },
];

interface GiftCardsToolbarProps {
  query: string;
  onQueryChange: (q: string) => void;
  status: GiftCardStatusFilter;
  onStatusChange: (v: GiftCardStatusFilter) => void;
  claimed: GiftCardClaimedFilter;
  onClaimedChange: (v: GiftCardClaimedFilter) => void;
  count: number;
  total: number;
  loading?: boolean;
}

/**
 * Sales → Gift Cards toolbar. Same look and interaction as the Transactions /
 * Payments toolbar (shared primitives from `salesToolbarParts`), adapted to the
 * gift-card filter set: Status + Claim status. There's no date-range window —
 * the tab intentionally lists every card so the tiles reflect all of them.
 * Non-default filters surface as pills; the `+ Filter` popover edits them.
 */
export default function GiftCardsToolbar({
  query,
  onQueryChange,
  status,
  onStatusChange,
  claimed,
  onClaimedChange,
  count,
  total,
  loading = false,
}: GiftCardsToolbarProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const statusLabel =
    STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
  const claimedLabel =
    CLAIMED_OPTIONS.find((o) => o.value === claimed)?.label ?? claimed;

  return (
    <Box className={styles.toolbar}>
      <SalesSearchInput
        value={query}
        onChange={onQueryChange}
        placeholder="Search code, client, transaction id"
      />

      {status !== "all" && (
        <MenuToken
          label="Status"
          valueLabel={statusLabel}
          current={status}
          options={STATUS_OPTIONS}
          onPick={(v) => onStatusChange(v as GiftCardStatusFilter)}
        />
      )}
      {claimed !== "all" && (
        <MenuToken
          label="Claimed"
          valueLabel={claimedLabel}
          current={claimed}
          options={CLAIMED_OPTIONS}
          onPick={(v) => onClaimedChange(v as GiftCardClaimedFilter)}
        />
      )}

      <Popover
        opened={popoverOpen}
        onChange={setPopoverOpen}
        position="bottom-start"
        width={268}
        radius={12}
        shadow="0 8px 24px rgba(0,0,0,.16)"
        withinPortal
      >
        <Popover.Target>
          <UnstyledButton
            className={`${styles.filterTrigger} ${
              popoverOpen ? styles.filterTriggerActive : ""
            }`}
            onClick={() => setPopoverOpen((o) => !o)}
          >
            <IconPlus size={13} stroke={2.4} />
            Filter
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap={13}>
            <Box>
              <Text className={styles.fieldLabel}>Status</Text>
              <Select
                data={STATUS_OPTIONS}
                value={status}
                onChange={(v) => v && onStatusChange(v as GiftCardStatusFilter)}
                allowDeselect={false}
                comboboxProps={{ withinPortal: false }}
              />
            </Box>
            <Box>
              <Text className={styles.fieldLabel}>Claim status</Text>
              <Select
                data={CLAIMED_OPTIONS}
                value={claimed}
                onChange={(v) =>
                  v && onClaimedChange(v as GiftCardClaimedFilter)
                }
                allowDeselect={false}
                comboboxProps={{ withinPortal: false }}
              />
            </Box>
          </Stack>
        </Popover.Dropdown>
      </Popover>

      {loading && <Loader size="xs" color="var(--brand-teal)" />}

      <Box style={{ flex: 1 }} />

      <ResultCount count={count} total={total} itemLabel="gift cards" />
    </Box>
  );
}
